import { Download, ExternalLink, Heart, Play, Terminal, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SpritePlayer, downloadBlob, exportStateGif } from "codexpetlab-player";
import type { StandardState } from "codexpetlab-core";
import type { Pet } from "../data/pets";
import { petCodexInstallUrl } from "../lib/install";
import { petDownloadPath } from "../lib/pet-contract";

const STATES: Array<{ key: StandardState; label: string }> = [
  { key: "idle", label: "Idle" },
  { key: "running", label: "Running" },
  { key: "waiting", label: "Waiting" },
  { key: "review", label: "Review" },
];

export function PetInspector({ pet, liked, onClose, onLike, onOpenDetails }: { pet: Pet; liked: boolean; onClose: () => void; onLike: () => void; onOpenDetails: () => void }) {
  const { t } = useTranslation();
  const [state, setState] = useState<StandardState>("idle");
  const [exporting, setExporting] = useState(false);

  const exportGif = async () => {
    setExporting(true);
    try {
      const blob = await exportStateGif({ src: `/api/pets/${pet.id}/spritesheet`, state });
      downloadBlob(blob, `${pet.id}-${state}.gif`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <aside className="pet-inspector" aria-label={`${pet.name} inspector`}>
      <header className="inspector-header"><div><span className="eyebrow">{t("inspector.title")}</span><h2>{pet.name}</h2></div><button className="icon-button" type="button" onClick={onClose} aria-label="Close inspector"><X size={19} /></button></header>
      <div className="inspector-stage"><SpritePlayer src={`/api/pets/${pet.id}/spritesheet`} state={state} width={280} aria-label={`${pet.name} ${state} animation`} /></div>
      <div className="inspector-states">{STATES.map((item) => <button key={item.key} className={state === item.key ? "active" : ""} type="button" onClick={() => setState(item.key)}>{item.label}</button>)}</div>
      <p className="inspector-description">{pet.description}</p>
      <div className="tag-list large">{pet.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
      <dl className="inspector-meta"><div><dt>{t("inspector.contract")}</dt><dd>V2 only</dd></div><div><dt>{t("inspector.atlas")}</dt><dd>1536×2288</dd></div><div><dt>{t("inspector.directions")}</dt><dd>16</dd></div><div><dt>{t("inspector.creator")}</dt><dd>@{pet.creatorHandle}</dd></div></dl>
      <div className="inspector-primary-actions"><a className="primary-button" href={petCodexInstallUrl(pet)}><Terminal size={16} /> {t("inspector.install")}</a><button className={liked ? "secondary-button liked" : "secondary-button"} type="button" onClick={onLike}><Heart size={16} fill={liked ? "currentColor" : "none"} /></button></div>
      <div className="inspector-secondary-actions"><a href={petDownloadPath(pet)} download={`${pet.id}.codex-pet.zip`}><Download size={15} /> ZIP</a><button type="button" onClick={() => void exportGif()} disabled={exporting}><Play size={15} /> {exporting ? "…" : "GIF"}</button><button type="button" onClick={onOpenDetails}><ExternalLink size={15} /> {t("inspector.details")}</button></div>
    </aside>
  );
}
